import React, { useMemo, useState } from 'react';
import { Message } from 'rsuite';
import { ConfirmModal } from '@nsi-advisory-services/components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  useDeleteVacationPlan,
  useEmployeePatoReports,
  useCloseDates,
  useTimeOffRequests,
  useVacationPlans,
} from '../hooks/useQueries';
import { PAYROLL_ENABLED } from '../config/payrollFeature';
import { buildPatoOutlook, patoBookedBetween } from '../utils/patoOutlook';
import PageHeader from '../components/shared/PageHeader';
import VacationPlannerNotice from '../components/timeoff/VacationPlannerNotice';
import { closeDateKeySet, formatDateOnly } from '../utils/timeOffDates';
import { formatSavedAt, formatTripRange, hrs, plural } from '../utils/vacationPlanFormat';
import { isEnteredRequest } from '../utils/vacationPlan';
import { showToast } from '../utils/toast';
import './TimeOffPage.css';

/*
  Vacation Planner: the way in.

  The employee's saved plans, newest first, each opening in the planner at
  /time-off/planner/:id. With none saved yet it is a single invitation to make
  the first one. New plans start at /time-off/planner/new.

  The figures on each plan are as they were when it was saved - the planner
  works them out afresh when the plan is opened. PATO is the exception: above
  the list is the balance today and where it would be at year end if every plan
  still ahead were taken, and a trip already behind us shows the PATO actually
  booked on its dates rather than what was planned (utils/patoOutlook.js).
*/

const NEW_PLAN_PATH = '/time-off/planner/new';

// What became of one submitted request, in the words the card uses. A request
// no longer in the list was deleted.
const requestOutcome = (request) => {
  if (!request) return 'removed';
  const status = String(request.status || '').toLowerCase();
  if (status === 'pending') return 'awaiting review';
  if (status === 'approved') return isEnteredRequest(request) ? 'entered' : 'approved';
  if (status === 'rejected') return 'rejected';
  if (status === 'cancelled') return 'withdrawn';
  return status || 'removed';
};

// The order the outcomes are listed in on a card.
const OUTCOME_ORDER = ['awaiting review', 'approved', 'entered', 'rejected', 'withdrawn', 'removed'];

/*
  A plan's submission status for its card, from what was recorded on the plan
  and what has become of those requests since:

    not submitted    nothing recorded
    partly submitted fewer requests recorded than the plan made
    submitted        all of them, with what became of each
    withdrawn        all of them, every one since withdrawn or removed
*/
const planSubmissionStatus = (plan, requestsById) => {
  const submission = plan.submission || null;
  const ids = Array.isArray(submission?.requestIds) ? submission.requestIds.map(String) : [];
  if (ids.length === 0) {
    return { tone: 'none', icon: 'bi-circle', label: 'Not submitted', detail: null };
  }

  const counts = new Map();
  ids.forEach((id) => {
    const outcome = requestOutcome(requestsById.get(id));
    counts.set(outcome, (counts.get(outcome) || 0) + 1);
  });
  const detail = OUTCOME_ORDER
    .filter((outcome) => counts.has(outcome))
    .map((outcome) => `${counts.get(outcome)} ${outcome}`)
    .join(' · ');
  const when = submission.lastSubmittedAt || submission.submittedAt;
  const since = when ? ` on ${formatSavedAt(when)}` : '';

  const gone = (counts.get('withdrawn') || 0) + (counts.get('removed') || 0);
  if (gone === ids.length) {
    return { tone: 'off', icon: 'bi-x-circle', label: 'Withdrawn', detail };
  }
  const total = Number(submission.total) || ids.length;
  if (ids.length < total) {
    return {
      tone: 'partial',
      icon: 'bi-circle-half',
      label: 'Partly submitted',
      detail: `${ids.length} of ${plural(total, 'request')}${since} · ${detail}`,
    };
  }
  return {
    tone: 'done',
    icon: 'bi-check-circle-fill',
    label: 'Submitted',
    detail: `${plural(ids.length, 'request')}${since} · ${detail}`,
  };
};

export default function VacationPlansPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const employeeId = auth.employee?.id ? String(auth.employee.id) : null;

  const plansQuery = useVacationPlans({ enabled: Boolean(employeeId) });
  // To say what became of each plan's requests. The list holds the employee's
  // own requests whatever their status.
  const requestsQuery = useTimeOffRequests({ enabled: Boolean(employeeId) });
  const closeDatesQuery = useCloseDates({ enabled: true });
  const requestsById = useMemo(() => {
    const map = new Map();
    (requestsQuery.data || []).forEach((row) => map.set(String(row.id), row));
    return map;
  }, [requestsQuery.data]);
  const deletePlanMutation = useDeleteVacationPlan();
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Everything above today's balance - trips and requests already behind us -
  // is folded away until asked for. It is history, and already in the balance.
  const [showPrevious, setShowPrevious] = useState(false);

  // In trip order, soonest first - the order the balance is spent in.
  const plans = useMemo(() => (
    (Array.isArray(plansQuery.data) ? plansQuery.data : [])
      .slice()
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date))
        || String(a.end_date).localeCompare(String(b.end_date)))
  ), [plansQuery.data]);
  const todayIso = formatDateOnly(new Date());
  const currentYear = Number(todayIso.slice(0, 4));
  const hasPlans = plans.length > 0;

  // The PATO report for this year (today's balance), and for every year a trip
  // already started in (what was actually booked on its dates).
  const reportYears = useMemo(() => {
    const years = new Set([currentYear]);
    plans.forEach((plan) => {
      if (String(plan.start_date) > todayIso) return;
      const last = Math.min(Number(String(plan.end_date).slice(0, 4)), currentYear);
      for (let year = Number(String(plan.start_date).slice(0, 4)); year <= last; year += 1) years.add(year);
    });
    return [...years].sort();
  }, [currentYear, plans, todayIso]);
  const reportQueries = useEmployeePatoReports(employeeId, reportYears, {
    enabled: PAYROLL_ENABLED && hasPlans,
  });
  const reportsLoading = reportQueries.some((query) => query.isLoading);
  // The query results are a fresh array every render, so the memo is keyed on
  // when each one's data last changed instead.
  const reportsKey = reportQueries.map((query) => query.dataUpdatedAt).join('|');
  const reportsByYear = useMemo(() => {
    const map = new Map();
    reportQueries.forEach((query, index) => {
      if (query.data) map.set(reportYears[index], query.data);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportYears, reportsKey]);

  const outlook = useMemo(() => buildPatoOutlook({
    plans,
    reportsByYear,
    requests: (requestsQuery.data || []).filter((row) => String(row.employee_id) === employeeId),
    closeKeys: closeDateKeySet(closeDatesQuery.data),
    todayIso,
  }), [closeDatesQuery.data, employeeId, plans, reportsByYear, requestsQuery.data, todayIso]);
  const showPato = PAYROLL_ENABLED && hasPlans;
  // The PATO column only once there is a balance to run down.
  const ledgerReady = showPato && !reportsLoading && !outlook.unlimited && outlook.balanceToday != null;

  /*
    The rows, top to bottom. Plans in date order; with the PATO column, the
    "Balance today" row goes in where today falls - after the trips already
    taken, before the first one still to come - and each later row's balance
    is the one above it less that row's PATO, so the column adds up as read.
    Other PATO requested and year end close it off.
  */
  /*
    PATO time off already approved and taken this year, outside any plan. Like
    a trip already behind us it is in today's balance, so it sits above that
    row - at the PATO TimeCamp actually booked on its dates, or its approved
    hours when that cannot be read. Requests a plan submitted show under the
    plan instead.
  */
  const pastPatoRequests = useMemo(() => {
    const planRequestIds = new Set(plans.flatMap((plan) => (plan.submission?.requestIds || []).map(String)));
    return (requestsQuery.data || [])
      .filter((request) => String(request.employee_id) === employeeId)
      .filter((request) => String(request.status || '').toLowerCase() === 'approved' && !isEnteredRequest(request))
      .filter((request) => String(request.end_date).slice(0, 10) < todayIso)
      .filter((request) => Number(String(request.start_date).slice(0, 4)) === currentYear)
      .filter((request) => !planRequestIds.has(String(request.id)))
      .map((request) => {
        const start = String(request.start_date).slice(0, 10);
        const end = String(request.end_date).slice(0, 10);
        return {
          request,
          start,
          end,
          approvedHours: Number(request.total_hours) || 0,
          actualPato: patoBookedBetween(start, end, reportsByYear),
        };
      });
  }, [currentYear, employeeId, plans, reportsByYear, requestsQuery.data, todayIso]);

  const ledgerRows = useMemo(() => {
    if (!ledgerReady) return plans.map((row) => ({ type: 'plan', row, pato: outlook.perPlan.get(row.id) }));

    const rows = [];
    let running = outlook.balanceToday;
    let todayPlaced = false;
    const placeToday = () => {
      if (todayPlaced) return;
      rows.push({ type: 'today', balance: running });
      todayPlaced = true;
    };

    // Everything already behind us first, then the rest, each in date order -
    // so past plans and past requests are all above today's balance.
    const entries = [
      ...plans.map((row) => {
        const pato = outlook.perPlan.get(row.id);
        return { kind: 'plan', row, pato, past: pato?.timing === 'past', date: String(row.start_date) };
      }),
      ...pastPatoRequests.map((item) => ({ kind: 'request', item, past: true, date: item.start })),
    ].sort((a, b) => (Number(b.past) - Number(a.past)) || a.date.localeCompare(b.date));

    entries.forEach((entry) => {
      if (entry.kind === 'request') {
        rows.push({ type: 'request', ...entry.item, previous: true });
        return;
      }
      const { row, pato } = entry;
      if (!pato) return;
      if (pato.timing !== 'past') placeToday();
      let balanceAfter = null;
      if (pato.timing !== 'past' && pato.balanceYear === currentYear) {
        running = Math.round((running - pato.stillToCome) * 100) / 100;
        balanceAfter = running;
      }
      rows.push({ type: 'plan', row, pato, balanceAfter, previous: pato.timing === 'past' });
    });
    placeToday();

    if (outlook.otherRequestedHours > 0) {
      running = Math.round((running - outlook.otherRequestedHours) * 100) / 100;
      rows.push({ type: 'other', hours: outlook.otherRequestedHours, balanceAfter: running });
    }
    rows.push({ type: 'yearEnd', balance: running });
    return rows;
  }, [currentYear, ledgerReady, outlook, pastPatoRequests, plans]);

  const previousRows = ledgerRows.filter((row) => row.previous);
  const currentRows = ledgerRows.filter((row) => !row.previous);
  const previousCount = previousRows.length;

  // What one plan does to the balance, for its cell in the PATO column.
  const renderPatoCell = ({ pato, balanceAfter }) => {
    if (pato.timing === 'past') {
      return pato.actualPato == null ? (
        <>
          <span className="timeoff-planner-plangrid__value bdc-num timeoff-planner-plangrid__value--muted">-</span>
          <span className="timeoff-planner-plangrid__sub">actual PATO not available</span>
        </>
      ) : (
        <>
          <span className="timeoff-planner-plangrid__value bdc-num timeoff-planner-plangrid__value--muted">{hrs(pato.actualPato)}</span>
          <span className="timeoff-planner-plangrid__sub">used · actual · already in today&apos;s balance</span>
        </>
      );
    }
    if (pato.balanceYear > currentYear) {
      return (
        <>
          <span className="timeoff-planner-plangrid__value bdc-num timeoff-planner-plangrid__value--muted">{hrs(pato.plannedPato)}</span>
          <span className="timeoff-planner-plangrid__sub">from your {pato.balanceYear} PATO</span>
        </>
      );
    }
    return (
      <>
        <span className={`timeoff-planner-plangrid__value bdc-num${pato.stillToCome > 0 ? ' bdc-text-warn' : ''}`}>
          {pato.stillToCome > 0 ? `- ${hrs(pato.stillToCome)}` : 'No PATO'}
        </span>
        {pato.timing === 'ongoing' && pato.actualPato != null ? (
          <span className="timeoff-planner-plangrid__sub">still to come · {hrs(pato.actualPato)} used so far</span>
        ) : null}
        <span className={`timeoff-planner-plangrid__sub${balanceAfter < 0 ? ' bdc-text-bad' : ''}`}>leaves {hrs(balanceAfter)}</span>
      </>
    );
  };

  // One saved plan's card.
  const renderCard = (row, style) => {
    const summary = row.summary || {};
    const isPast = row.end_date < todayIso;
    const status = planSubmissionStatus(row, requestsById);
    const pato = outlook.perPlan.get(row.id);
    return (
      <div
        key={row.id}
        className="timeoff-planner-saved__item timeoff-planner-reveal"
        style={style}
      >
        <button
          type="button"
          className="timeoff-planner-saved__open"
          onClick={() => navigate(`/time-off/planner/${row.id}`)}
        >
          <div className="timeoff-planner-saved__name">
            {row.name}
            {isPast ? <span className="timeoff-planner-saved__tag">Past</span> : null}
          </div>
          <div className="timeoff-planner-saved__meta">
            {formatTripRange(row.start_date, row.end_date)} · saved {formatSavedAt(row.updated_at)}
          </div>
          {summary.totalHours != null ? (
            <div className="timeoff-planner-saved__figures">
              <span>{hrs(summary.totalHours)} off</span>
              <span className="bdc-text-good">{hrs(summary.enteredHours ?? 0)} Entered</span>
              {!showPato || !pato ? (
                <span className={summary.patoHours > 0 ? 'bdc-text-warn' : ''}>{hrs(summary.patoHours ?? 0)} PATO</span>
              ) : pato.timing === 'past' ? (
                pato.actualPato == null ? (
                  <span title="What TimeCamp booked could not be read, so this is the plan's figure.">
                    {reportsLoading ? 'PATO used …' : `${hrs(pato.plannedPato)} PATO planned`}
                  </span>
                ) : (
                  <span className={pato.actualPato > 0 ? 'bdc-text-warn' : ''}>
                    {hrs(pato.actualPato)} PATO used <span className="timeoff-planner-saved__actual">actual</span>
                  </span>
                )
              ) : pato.timing === 'ongoing' ? (
                <span className="bdc-text-warn">
                  {pato.actualPato == null ? '' : `${hrs(pato.actualPato)} PATO used so far · `}
                  {hrs(pato.stillToCome)} still to come
                </span>
              ) : (
                <span className={pato.plannedPato > 0 ? 'bdc-text-warn' : ''}>{hrs(pato.plannedPato)} PATO planned</span>
              )}
            </div>
          ) : null}
          {status.detail ? (
            <div className="timeoff-planner-saved__submission">
              {requestsQuery.isLoading ? 'Checking your requests…' : status.detail}
            </div>
          ) : null}
        </button>
        <div className="timeoff-planner-saved__actions">
          <span className={`timeoff-planner-status timeoff-planner-status--${status.tone}`}>
            <i className={`bi ${status.icon}`} aria-hidden="true" />
            {status.label}
          </span>
          <button
            type="button"
            className="bdc-btn bdc-btn--sm"
            onClick={() => navigate(`/time-off/planner/${row.id}`)}
          >
            <i className="bi bi-folder2-open" aria-hidden="true" />
            Open
          </button>
          <button
            type="button"
            className="bdc-btn bdc-btn--sm bdc-btn--danger"
            onClick={() => setDeleteTarget(row)}
            aria-label={`Delete ${row.name}`}
          >
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  };

  // One row of the plans grid: a line of the PATO sum and what it belongs to.
  const renderRow = (item, index) => {
    const delay = { animationDelay: `${Math.min(index, 12) * 40}ms` };

    if (item.type === 'today') {
      return (
        <React.Fragment key="today">
          <div className="timeoff-planner-plangrid__cell timeoff-planner-plangrid__cell--today timeoff-planner-reveal" style={delay}>
            <span className="timeoff-planner-plangrid__label">Balance today</span>
            <span className="timeoff-planner-plangrid__value bdc-num">{hrs(item.balance)}</span>
          </div>
          <div className="timeoff-planner-plangrid__today timeoff-planner-reveal" style={delay}>
            <span>Today · {formatSavedAt(new Date())}</span>
          </div>
        </React.Fragment>
      );
    }

    if (item.type === 'request') {
      const { request } = item;
      const openRequest = () => navigate(`/time-off/${request.id}`);
      return (
        <React.Fragment key={`request-${request.id}`}>
          <div className="timeoff-planner-plangrid__cell timeoff-planner-plangrid__cell--past timeoff-planner-reveal" style={delay}>
            <span className="timeoff-planner-plangrid__value bdc-num timeoff-planner-plangrid__value--muted">
              {hrs(item.actualPato ?? item.approvedHours)}
            </span>
            <span className="timeoff-planner-plangrid__sub">
              {item.actualPato != null ? 'used · actual' : 'approved'} · already in today&apos;s balance
            </span>
          </div>
          <div className="timeoff-planner-saved__item timeoff-planner-saved__item--request timeoff-planner-reveal" style={delay}>
            <button type="button" className="timeoff-planner-saved__open" onClick={openRequest}>
              <div className="timeoff-planner-saved__name">{formatTripRange(item.start, item.end)}</div>
              <div className="timeoff-planner-saved__meta">
                PATO time off · {hrs(item.approvedHours)} approved
              </div>
              {request.notes ? (
                <div className="timeoff-planner-saved__notes">{request.notes}</div>
              ) : null}
            </button>
            <div className="timeoff-planner-saved__actions">
              <span className="timeoff-planner-status timeoff-planner-status--done">
                <i className="bi bi-check-circle-fill" aria-hidden="true" />
                Approved
              </span>
              <button type="button" className="bdc-btn bdc-btn--sm" onClick={openRequest}>
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                View
              </button>
            </div>
          </div>
        </React.Fragment>
      );
    }

    if (item.type === 'other') {
      return (
        <React.Fragment key="other">
          <div className="timeoff-planner-plangrid__cell timeoff-planner-reveal" style={delay}>
            <span className="timeoff-planner-plangrid__value bdc-num bdc-text-warn">- {hrs(item.hours)}</span>
            <span className="timeoff-planner-plangrid__sub">leaves {hrs(item.balanceAfter)}</span>
          </div>
          <div className="timeoff-planner-plangrid__note timeoff-planner-reveal" style={delay}>
            Other PATO you have already requested this year, outside your plans.
          </div>
        </React.Fragment>
      );
    }

    if (item.type === 'yearEnd') {
      const over = item.balance < 0;
      return (
        <React.Fragment key="year-end">
          <div className={`timeoff-planner-plangrid__cell timeoff-planner-plangrid__cell--total timeoff-planner-reveal${over ? ' bdc-text-bad' : ''}`} style={delay}>
            <span className="timeoff-planner-plangrid__label">At year end</span>
            <span className={`timeoff-planner-plangrid__value bdc-num${over ? ' bdc-text-bad' : ' bdc-text-good'}`}>{hrs(item.balance)}</span>
          </div>
          <div className="timeoff-planner-plangrid__note timeoff-planner-reveal" style={delay}>
            {over
              ? `Taking every plan would leave you ${hrs(Math.abs(item.balance))} over your balance by the end of ${currentYear}.`
              : `If you take every plan still ahead, you would end ${currentYear} with ${hrs(item.balance)} of PATO.`}
            {' '}Trips already taken are in today's balance at what was actually booked.
          </div>
        </React.Fragment>
      );
    }

    const { row } = item;
    return (
      <React.Fragment key={row.id}>
        {ledgerReady ? (
          <div className={`timeoff-planner-plangrid__cell timeoff-planner-plangrid__cell--${item.pato.timing} timeoff-planner-reveal`} style={delay}>
            {renderPatoCell(item)}
          </div>
        ) : null}
        {renderCard(row, delay)}
      </React.Fragment>
    );
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePlanMutation.mutateAsync(deleteTarget.id);
      showToast('success', 'Plan deleted.');
      setDeleteTarget(null);
    } catch (error) {
      showToast('error', error?.message || 'The plan could not be deleted.');
    }
  };

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Time Off', to: '/time-off' },
          { label: 'Vacation Planner' },
        ]}
        icon="bi-airplane"
        title="Vacation Planner"
        count={hasPlans ? plans.length : null}
        actions={hasPlans ? (
          <button type="button" className="bdc-btn bdc-btn--primary" onClick={() => navigate(NEW_PLAN_PATH)}>
            <i className="bi bi-plus-lg" aria-hidden="true" />
            New plan
          </button>
        ) : null}
      />

      <VacationPlannerNotice />

      {!employeeId ? (
        <Message type="warning" showIcon>
          Your account is not linked to an employee record, so there is nothing to plan against.
        </Message>
      ) : plansQuery.isError ? (
        <Message type="error" showIcon>
          {String(plansQuery.error?.message || 'Your saved plans could not be loaded.')}
        </Message>
      ) : plansQuery.isLoading ? (
        <div className="timeoff-planner-details">
          <div className="timeoff-planner-card timeoff-planner-card--working" role="status">
            <span className="timeoff-planner-spinner" aria-hidden="true" />
            <span className="timeoff-planner-working__message">Loading your plans…</span>
          </div>
        </div>
      ) : !hasPlans ? (
        /* Nothing saved yet: one thing to do, so one big button. */
        <div className="timeoff-planner-details">
          <button
            type="button"
            className="timeoff-planner-card timeoff-planner-empty timeoff-planner-reveal"
            onClick={() => navigate(NEW_PLAN_PATH)}
          >
            <span className="timeoff-planner-empty__icon" aria-hidden="true">
              <i className="bi bi-airplane" />
            </span>
            <span className="timeoff-planner-ask">You have no vacation plans yet</span>
            <span className="timeoff-planner-sub">
              Work out how much of a trip your reduction covers, how much needs PATO, and which dates to request
              each way - then save it or submit it.
            </span>
            <span className="bdc-btn bdc-btn--primary timeoff-planner-empty__cta">
              <i className="bi bi-plus-lg" aria-hidden="true" />
              Click to create a new plan
            </span>
          </button>
        </div>
      ) : (
        /* The plans in date order, each with its PATO beside it, so the sum
           reads down the left as the plans read down the right: trips already
           taken (actual PATO, in today's balance), today's balance, each trip
           ahead taking its PATO off, other PATO requested, and year end. */
        <div className={`timeoff-planner-plangrid${ledgerReady ? ' timeoff-planner-plangrid--pato' : ''}`}>
          {showPato && !ledgerReady ? (
            <div className="timeoff-planner-outlook timeoff-planner-plangrid__wide">
              <div className="timeoff-planner-outlook__title">PATO in {currentYear}</div>
              <div className="timeoff-planner-outlook__note">
                {reportsLoading
                  ? 'Working out your balance…'
                  : outlook.unlimited
                    ? 'Unlimited time off under the company policy - there is no balance to run down.'
                    : 'Your PATO balance could not be read from TimeCamp just now, so it cannot be projected.'}
              </div>
            </div>
          ) : null}

          {ledgerReady ? (
            <>
              <div className="timeoff-planner-plangrid__colhead">PATO in {currentYear}</div>
              <div className="timeoff-planner-plangrid__colhead">
                {pastPatoRequests.length > 0 ? 'Your plans and time off' : 'Your plans'}
              </div>
            </>
          ) : null}

          {previousCount > 0 ? (
            <div className="timeoff-planner-plangrid__wide timeoff-planner-plangrid__toggle">
              <button
                type="button"
                className="bdc-btn bdc-btn--sm"
                aria-expanded={showPrevious}
                aria-controls="previous-requests"
                onClick={() => setShowPrevious((open) => !open)}
              >
                <i className={`bi ${showPrevious ? 'bi-arrow-down' : 'bi-arrow-up'}`} aria-hidden="true" />
                {showPrevious ? 'Hide previous requests' : `View previous requests (${previousCount})`}
              </button>
            </div>
          ) : null}

          {/* Earlier requests and trips, folded. Always rendered so the fold
              can open smoothly: its height animates from nothing to the rows'
              own (the grid-template-rows 0fr -> 1fr trick), and it is inert
              while closed so nothing inside can be tabbed to. */}
          {previousCount > 0 ? (
            <div
              id="previous-requests"
              className={`timeoff-planner-plangrid__fold${showPrevious ? ' timeoff-planner-plangrid__fold--open' : ''}`}
              aria-hidden={!showPrevious}
              inert={!showPrevious}
            >
              <div className="timeoff-planner-plangrid__fold-inner">
                <div className="timeoff-planner-plangrid timeoff-planner-plangrid--pato timeoff-planner-plangrid--nested">
                  {previousRows.map(renderRow)}
                </div>
              </div>
            </div>
          ) : null}

          {currentRows.map(renderRow)}
        </div>
      )}

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Delete Plan"
        body={(
          <>
            Delete <strong>{deleteTarget?.name}</strong>? This only removes the saved plan - it does not touch any time
            off you have requested.
          </>
        )}
        confirmText="Delete"
        danger
        loading={deletePlanMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
